import type { AuthMethod } from "@defuse-protocol/internal-utils"
import { QuoteRequest } from "@defuse-protocol/one-click-sdk-typescript"
import type { BalanceMapping } from "@src/components/DefuseSDK/features/machines/depositedBalanceMachine"
import type {
  BaseTokenInfo,
  TokenInfo,
} from "@src/components/DefuseSDK/types/base"
import { isSamePaymentFamily, resolvePaymentSource } from "@src/components/DefuseSDK/utils/paymentTokenSelection"
import { selectQuoteInputToken } from "@src/components/DefuseSDK/utils/quoteInputTokenSelection"
import { getTokenAid, isBaseToken } from "@src/components/DefuseSDK/utils/token"
import { adjustDecimalsTokenValue } from "@src/components/DefuseSDK/utils/tokenUtils"
import { isNearIntentsNetwork } from "@src/shared/utils/networks"
import type { SnapshotFrom } from "xstate"
import type { WithdrawQuote1csInput } from "../machines/backgroundWithdraw1csQuoterMachine"
import type { withdrawFormReducer } from "../machines/withdrawFormReducer"
import {
  parsedWithdrawRecipientMatchesNetwork,
  resolveWithdrawQuoteRecipient,
  withdrawQuoteRecipientsEqual,
} from "./withdrawRecipient"

type WithdrawFormContext = SnapshotFrom<typeof withdrawFormReducer>["context"]
export type ExactInputAmount = { amount: bigint; decimals: number }

export function getSiblingCandidates(
  tokenIn: TokenInfo,
  tokenList: TokenInfo[]
): BaseTokenInfo[] | undefined {
  if (!isBaseToken(tokenIn)) return undefined
  return tokenList.filter((token): token is BaseTokenInfo => {
    if (!isBaseToken(token)) return false
    if (token.defuseAssetId === tokenIn.defuseAssetId) return true
    const tokenInAid = getTokenAid(tokenIn)
    const tokenAid = getTokenAid(token)
    if (tokenInAid != null && tokenAid != null) return tokenInAid === tokenAid
    return token.symbol === tokenIn.symbol
  })
}

export function resolveWithdrawQuoteInput(params: {
  formContext: Pick<WithdrawFormContext, "paymentToken" | "tokenIn">
  amount: { amount: bigint; decimals: number }
  amountMode: QuoteRequest.swapType
  balances: BalanceMapping
  tokenList: TokenInfo[]
  exactInputAmount?: ExactInputAmount | null
}): {
  tokenIn: BaseTokenInfo
  amount: { amount: bigint; decimals: number }
} | null {
  const {
    formContext,
    amount,
    amountMode,
    balances,
    tokenList,
    exactInputAmount,
  } = params

  if (isSamePaymentFamily(formContext.paymentToken, formContext.tokenIn)) {
    return selectQuoteInputToken({
      tokenIn: formContext.paymentToken,
      parsedAmount: amount,
      balances,
      siblingCandidates: getSiblingCandidates(
        formContext.paymentToken,
        tokenList
      ),
    })
  }

  const source = resolvePaymentSource({
    paymentToken: formContext.paymentToken,
    balances,
    siblingCandidates: getSiblingCandidates(
      formContext.paymentToken,
      tokenList
    ),
  })
  if (source == null) return null

  if (amountMode === QuoteRequest.swapType.EXACT_INPUT) {
    const sourceAmount = exactInputAmount ?? source.spendableBalance
    if (sourceAmount.amount === 0n) return null
    return {
      tokenIn: source.quoteToken,
      amount: sourceAmount,
    }
  }

  return {
    tokenIn: source.quoteToken,
    amount,
  }
}

export function matchesWithdrawQuoteInput(params: {
  formContext: WithdrawFormContext
  quoteInput: WithdrawQuote1csInput
  userAddress: string | null
  userChainType: AuthMethod | null
  tokenList: TokenInfo[]
  balances: BalanceMapping
  exactInputAmount?: ExactInputAmount | null
  isConfidential?: boolean
}): boolean {
  const {
    formContext,
    quoteInput,
    userAddress,
    userChainType,
    tokenList,
    balances,
    exactInputAmount,
    isConfidential,
  } = params

  if (
    formContext.parsedAmount == null ||
    formContext.parsedAmount.amount === 0n ||
    formContext.parsedRecipient == null ||
    !parsedWithdrawRecipientMatchesNetwork(
      formContext.parsedRecipient,
      isNearIntentsNetwork(formContext.blockchain)
    ) ||
    userAddress == null ||
    userChainType == null
  ) {
    return false
  }

  const { recipient } = resolveWithdrawQuoteRecipient({
    parsedRecipient: formContext.parsedRecipient,
    displayAddress: formContext.recipient,
    blockchain: formContext.blockchain,
    destinationChainName: formContext.tokenOutDeployment.chainName,
  })

  const destinationMemo = formContext.parsedDestinationMemo ?? null
  const quoteDestinationMemo = quoteInput.destinationMemo ?? null

  const amountInQuoteDecimals = adjustDecimalsTokenValue(
    formContext.parsedAmount,
    quoteInput.amount.decimals
  )

  const resolvedInput = resolveWithdrawQuoteInput({
    formContext,
    amount: amountInQuoteDecimals,
    amountMode: quoteInput.swapType,
    balances,
    tokenList,
    exactInputAmount,
  })

  if (
    resolvedInput == null ||
    resolvedInput.tokenIn.defuseAssetId !== quoteInput.tokenIn.defuseAssetId
  ) {
    return false
  }

  return (
    quoteInput.userAddress === userAddress &&
    quoteInput.userChainType === userChainType &&
    (quoteInput.isConfidential === true) === (isConfidential === true) &&
    withdrawQuoteRecipientsEqual(quoteInput.recipient, recipient) &&
    quoteInput.tokenOut.defuseAssetId === formContext.tokenOut.defuseAssetId &&
    resolvedInput.amount.amount === quoteInput.amount.amount &&
    destinationMemo === quoteDestinationMemo
  )
}
